import { FC, useEffect, useRef, useState } from "react";
import { FormType } from "../../common";
import { DairyV2Baseline } from "../../../../models/Baseline/DairyV2Baseline";
import { useIntl } from "../../../../../../_metronic/i18n/customUseIntl";
import { CSSClassesList } from "../../../../helpers/helperTypes";
import { processAndStageStylesV2 } from "../../../../../../_metronic/layout";
import { useFieldArray, useFormContext } from "react-hook-form";
import { DairyIntervention } from "../../../../models/Intervention";
import {
  CompoundFeedDatabase,
  MasterDataOption,
  Maybe,
  StageType
} from "../../../../../../graphql/types";
import {
  DairyV2ContinuousGrowingStageData,
  DairyV2DairyCowStageData
} from "../../../../models/Intervention/DairyV2Intervention";
import { CattleFeed } from "../../../../models/Intervention/CattleIntervention";
import { cloneDeep } from "lodash";
import { DialogContainer } from "../../CommonDataParts/DialogContainer2";
import { DsmGrid } from "@dsm-dcs/design-system-react";
import DsmButtonControlGroup from "../../../helpers/DsmButtonControlGroup";
import DairyV2FeedCompoundFeedComponent from "./DairyV2FeedCompoundFeedComponent";
import ReactHookDsmSelect from "../../../../../modules/Helpers/ReactHookDsmSelect2";
import { getDatabaseOptions } from "../../../CompoundFeedWizard/utils";
import DairyV2FeedSingleIngredientComponent from "./DairyV2FeedSingleIngredientComponent";
import { Origin } from "../types";
import DairyV2FeedDietCharacterisationDialog from "./DairyV2FeedDietCharacterisationDialog";
import DairyV2FeedFreshGrassComponent from "./DairyV2FeedFreshGrassComponent";
import DairyV2FeedMothersMilkComponent from "./DairyV2FeedMothersMilkComponent";
import { Spacer } from "../../../helpers/Spacer";
import { BaselineFeed, BaselineFeedWithOrigin } from "../../../../models/Baseline/Baseline";
import { FreshGrass } from "../../../../models/Baseline/CattleBaseline";
import { unitLong } from "../../../../utils/unit-utils";
import { UserProfilePrefs } from "../../../../../modules/Helpers/UserProfilePrefs";
import { dairyAshContentValue, dairyGrossEnergyContentValue } from "../interventionDefaultValues";

interface DairyV2FeedDialogProps {
  compoundFeeds?: Maybe<Array<MasterDataOption>>;
  singleIngredients: Maybe<Array<MasterDataOption>>;
  origins: Maybe<Array<Origin>>;
  formType: FormType;
  stageIndex: number;
  stageType: string;
  baseline: DairyV2Baseline;
  handleCancel: (param?: string | null) => void;
};

const DairyV2FeedDialog: FC<DairyV2FeedDialogProps> = ({
  compoundFeeds,
  singleIngredients,
  origins,
  formType = FormType.Add,
  stageIndex = 0,
  stageType,
  baseline,
  handleCancel,
}) => {
  const classes: CSSClassesList = processAndStageStylesV2();
  const intl = useIntl();
  const currResetValue = useRef<CattleFeed>();
  const fc = useFormContext<DairyIntervention>();

  const [isDietEdit, setIsDietEdit] = useState(false);
  const [isCompFeedEdit, setIsCompFeedEdit] = useState(false);
  const [isSingleIngEdit, setIsSingleIngEdit] = useState(false);
  const [isMotherMilkEdit, setIsMotherMilkEdit] = useState(false);
  const [isFreshGrassEdit, setIsFreshGrassEdit] = useState(false);
  const isContinuousGrowth = stageType === StageType.ContinuousGrowing;

  const formTitle = intl.formatMessage({ id: 'SUSTELL.PROCESS.DIALOG.STAGE.FEED.TITLE', });
  const fieldItemPrefix = `stages.${stageIndex}.stageData.feed`;

  const currentStageData = baseline.stages[stageIndex].stageData;

  const userUOM = UserProfilePrefs.getInstance().getUserUnitPrefs();
  const barnOutputMassUnitLong = userUOM?.unitBarnOutputMass ? unitLong(userUOM.unitBarnOutputMass) : null;

  const getLongUnitName = (unitShort: string | null): string => {
    if (unitShort === 'kg') {
      return 'kilograms';
    } else if (unitShort === 'lbs') {
      return 'pounds';
    } else {
      return 'kilograms';
    }
  };

  const getDMUnit = (unitShort: string | null): string => {
    if (unitShort === 'kg') {
      return 'kg DM';
    } else if (unitShort === 'lbs') {
      return 'lbs DM';
    } else {
      return 'kg DM';
    }
  };

  let dairyV2FeedData: CattleFeed;
  switch (stageType) {
    case StageType.ContinuousGrowing:
      dairyV2FeedData = (currentStageData as DairyV2ContinuousGrowingStageData).feed;
      break;

    case StageType.DairyCow:
      dairyV2FeedData = (currentStageData as DairyV2DairyCowStageData).feed;
      break;

    default:
      dairyV2FeedData = {} as CattleFeed;
      break;
  }

  const {
    fields: feedsAdditions,
    append: appendFeedsAdditions,
    remove: removeFeedsAdditions,
  } = useFieldArray({
    name: `${fieldItemPrefix}.compoundFeedsAdditions`,
    control: fc.control,
    keyName: "keyId",
  });

  const {
    fields: ingredientsAdditions,
    append: appendIngredientsAdditions,
    remove: removeIngredientsAdditions,
  } = useFieldArray({
    name: `${fieldItemPrefix}.singleIngredientsAdditions`,
    control: fc.control,
    keyName: "keyId",
  });

  const {
    fields: freshGrass,
    append: appendFreshGrass,
    remove: removeFreshGrass,
  } = useFieldArray({
    name: `${fieldItemPrefix}.freshGrassAdditions`,
    control: fc.control,
    keyName: 'keyId',
  });

  useEffect(() => {
    currResetValue.current = cloneDeep(fc.getValues(fieldItemPrefix));
  }, []);

  const dietCharacterisationCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.dietCharacterisation === undefined)
      fc.setValue(`${fieldItemPrefix}.dietCharacterisation`, "")

    if (isInnerCancel)
      fc.setValue(`${fieldItemPrefix}.dietCharacterisation`, currResetValue.current?.dietCharacterisation);

    setIsDietEdit(false);
  };

  const compoundFeedCancelHandler = (isInnerCancel = true) => {
    if ((currResetValue.current?.compoundFeeds?.length ?? 0) === 0) {
      (dairyV2FeedData?.compoundFeeds || [])?.filter((item) => !!item.feedType)?.forEach((compFeed, index) => {
        const fieldPrefix = `${fieldItemPrefix}.compoundFeeds[${index}]`;
        Object.keys(compFeed).forEach((key) => {
          fc.setValue(`${fieldPrefix}.${key}`, "");
        })
      });
    }

    const compFeedAdditions = fc.getValues(`${fieldItemPrefix}.compoundFeedsAdditions`) as BaselineFeed[];
    if ((compFeedAdditions?.length ?? 0) > 0) {
      for (let i = compFeedAdditions.length; i >= 0; i--) {
        removeFeedsAdditions(i);
      };
    }

    if ((currResetValue.current?.compoundFeedsAdditions?.length ?? 0) > 0) {
      currResetValue.current?.compoundFeedsAdditions?.forEach((compFeed) => {
        appendFeedsAdditions(compFeed);
      })
    }

    if (isInnerCancel) {
      fc.setValue(`${fieldItemPrefix}.compoundFeeds`, cloneDeep(currResetValue.current?.compoundFeeds));
      fc.setValue(`${fieldItemPrefix}.compoundFeedsAdditions`, cloneDeep(currResetValue.current?.compoundFeedsAdditions));
    }

    setIsCompFeedEdit(false);
  };

  const singleIngredientCancelHandler = (isInnerCancel = true) => {
    if ((currResetValue.current?.singleIngredients?.length ?? 0) === 0) {
      (dairyV2FeedData.singleIngredients || []).forEach((singIng, index) => {
        const fieldPrefix = `${fieldItemPrefix}.singleIngredients[${index}]`;
        Object.keys(singIng).forEach((key) => {
          fc.setValue(`${fieldPrefix}.${key}`, "");
        })
      });
    }

    const singIngAdditions = fc.getValues(`${fieldItemPrefix}.singleIngredientsAdditions`) as BaselineFeedWithOrigin[];
    if ((singIngAdditions?.length ?? 0) > 0) {
      for (let i = singIngAdditions.length; i >= 0; i--) {
        removeIngredientsAdditions(i);
      };
    }

    if ((currResetValue.current?.singleIngredientsAdditions?.length ?? 0) > 0) {
      currResetValue.current?.singleIngredientsAdditions?.forEach((singIng) => {
        appendIngredientsAdditions(singIng);
      });
    }

    if (isInnerCancel) {
      fc.setValue(`${fieldItemPrefix}.singleIngredients`, cloneDeep(currResetValue.current?.singleIngredients));
      fc.setValue(`${fieldItemPrefix}.singleIngredientsAdditions`, cloneDeep(currResetValue.current?.singleIngredientsAdditions));
    }

    setIsSingleIngEdit(false);
  };

  const mothersMilkCancelHandler = (isInnerCancel = true) => {
    if (currResetValue.current?.mothersMilk?.isMothersMilk === undefined)
      fc.setValue(`${fieldItemPrefix}.mothersMilk.isMothersMilk`, !!dairyV2FeedData.mothersMilk?.isMothersMilk);

    if (currResetValue.current?.mothersMilk?.amount === undefined)
      fc.setValue(`${fieldItemPrefix}.mothersMilk.amount`, "");

    if (currResetValue.current?.mothersMilk?.dryMatterContent === undefined)
      fc.setValue(`${fieldItemPrefix}.mothersMilk.dryMatterContent`, "");

    if (currResetValue.current?.mothersMilk?.crudeProteinContent === undefined)
      fc.setValue(`${fieldItemPrefix}.mothersMilk.crudeProteinContent`, "");

    if (currResetValue.current?.mothersMilk?.digestibility === undefined)
      fc.setValue(`${fieldItemPrefix}.mothersMilk.digestibility`, "");

    if (isInnerCancel) {
      fc.setValue(`${fieldItemPrefix}.mothersMilk`, cloneDeep(currResetValue.current?.mothersMilk));
    }

    setIsMotherMilkEdit(false);
  };

  const freshGrassCancelHandler = (isInnerCancel = true) => {
    if ((currResetValue.current?.freshGrass?.length ?? 0) === 0) {
      (dairyV2FeedData?.freshGrass || []).forEach((fGrass, index) => {
        const fieldPrefix = `${fieldItemPrefix}.freshGrass[${index}]`;
        Object.keys(fGrass).forEach((key) => {
          fc.setValue(`${fieldPrefix}.${key}`, "");
        })
      });
    }

    const fGrassAdditions = fc.getValues(`${fieldItemPrefix}.freshGrassAdditions`) as FreshGrass[];
    if ((fGrassAdditions?.length ?? 0) > 0) {
      for (let i = fGrassAdditions.length - 1; i >= 0; i--) {
        removeFreshGrass(i);
      };
    }

    if ((currResetValue.current?.freshGrassAdditions?.length ?? 0) > 0) {
      currResetValue.current?.freshGrassAdditions?.forEach((fGrass) => {
        appendFreshGrass(fGrass);
      });
    }

    if (isInnerCancel) {
      fc.setValue(`${fieldItemPrefix}.freshGrass`, cloneDeep(currResetValue.current?.freshGrass));
      fc.setValue(`${fieldItemPrefix}.freshGrassAdditions`, cloneDeep(currResetValue.current?.freshGrassAdditions));
    }

    setIsFreshGrassEdit(false);
  };

  const resetFreshGrassAdditionToDefaultValues = () => {
    freshGrass.forEach((_, index) => {
      fc.setValue(`${fieldItemPrefix}.freshGrassAdditions.${index}.grossEnergyContent`, dairyAshContentValue);
      fc.clearErrors(`${fieldItemPrefix}.freshGrassAdditions.${index}.grossEnergyContent`);

      fc.setValue(`${fieldItemPrefix}.freshGrassAdditions.${index}.ashContent`, dairyGrossEnergyContentValue);
      fc.clearErrors(`${fieldItemPrefix}.freshGrassAdditions.${index}.ashContent`);
    });
  };

  const handleDialogCancelClick = () => {
    dietCharacterisationCancelHandler(false);
    compoundFeedCancelHandler(false);
    singleIngredientCancelHandler(false);
    mothersMilkCancelHandler(false);
    freshGrassCancelHandler(false);

    fc.setValue(`${fieldItemPrefix}`, currResetValue.current);

    if (handleCancel) {
      handleCancel();
    }
  };

  let originalDietCharacterization: string | undefined = "";
  if (isContinuousGrowth) {
    originalDietCharacterization = (baseline?.stages[stageIndex]?.stageData as DairyV2ContinuousGrowingStageData)
      .feed.dietCharacterisation;
  }

  const getDialogDescription = () => {
    if (isContinuousGrowth) {
      return (
        <>
          {intl.formatMessage({ id: "SUSTELL.PROCESS.DIALOG.STAGE.ANIMALS.FEED.DESCRIPTION" })}
          <br /><br />
          {intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.FEED.CONTINUOUS_GROWING.NEW_DESCRIPTION" },
            {
              longUnit: getLongUnitName(barnOutputMassUnitLong),
              unit: getDMUnit(barnOutputMassUnitLong)
            }
          )}
        </>
      );
    }

    return (
      <>
        {intl.formatMessage({ id: "SUSTELL.PROCESS.DIALOG.STAGE.ANIMALS.FEED.DESCRIPTION" })}
        <br /><br />
        {intl.formatMessage({ id: "SUSTELL.STAGE.DAIRY.FEED.DAIRY_COW.DESCRIPTION" },
          {
            longUnit: getLongUnitName(barnOutputMassUnitLong),
            unit: getDMUnit(barnOutputMassUnitLong)
          }
        )}
      </>
    );
  };


  return (
    <DialogContainer
      formVisible={true}
      handleClose={handleDialogCancelClick}
      iconCode="arrows/arrow-circle-broken-right"
      formTitle={formTitle}
      introText={getDialogDescription()}
      datasetType="intervention"
      width="1272px"
      dialogContentStyles={
        {
          paddingBottom: '1.25rem',
          overflowY: 'unset',
        }
      }
    >
      <DsmGrid className={classes.dsmGridThreeColumnSecondBigOthersSmall}>
        <ReactHookDsmSelect
          name="databaseFoundation"
          defaultValue={baseline?.info?.databaseFoundation || CompoundFeedDatabase.Gfli}
          label={`${intl.formatMessage({ id: 'SUSTELL.STAGE.FEED.COMPOUND_FEED.DATABASE_SELETION' })}`}
          disabled
          options={getDatabaseOptions(fc.getValues(`reportingType`)) as []}
        />
        {isContinuousGrowth &&
          <DairyV2FeedDietCharacterisationDialog
            formType={formType}
            fieldItemPrefix={fieldItemPrefix}
            currentStageData={dairyV2FeedData}
            originalDietCharacterization={originalDietCharacterization}
            isDietEdit={isDietEdit}
            setIsDietEdit={setIsDietEdit}
            resetEdit={dietCharacterisationCancelHandler}
          />}
      </DsmGrid>
      <Spacer />
      <DsmGrid className={classes.dsmGridTwoColumn2To3}>
        <div>
          <p className={classes.headingTitleVariant}>
            {intl.formatMessage({ id: "SUSTELL.STAGE.FEED.COMPOUND_FEED" })}
          </p>
          <DairyV2FeedCompoundFeedComponent
            compoundFeeds={compoundFeeds}
            databaseFoundation={baseline?.info?.databaseFoundation || CompoundFeedDatabase.Gfli}
            formType={formType}
            fieldItemPrefix={fieldItemPrefix}
            currentStageData={dairyV2FeedData}
            isCompFeedEdit={isCompFeedEdit}
            setIsCompFeedEdit={setIsCompFeedEdit}
            feedsAdditions={feedsAdditions}
            appendFeedsAdditions={appendFeedsAdditions}
            removeFeedsAdditions={removeFeedsAdditions}
            resetEdit={compoundFeedCancelHandler}
          />
        </div>
        <div>
          <p className={classes.headingTitleVariant}>
            {intl.formatMessage({ id: "SUSTELL.STAGE.FEED.SINGLE_INGREDIENT" })}
          </p>
          <DairyV2FeedSingleIngredientComponent
            singleIngredients={singleIngredients}
            origins={origins}
            formType={formType}
            stageIndex={stageIndex}
            fieldItemPrefix={fieldItemPrefix}
            currentStageData={dairyV2FeedData}
            isSingleIngEdit={isSingleIngEdit}
            setIsSingleIngEdit={setIsSingleIngEdit}
            ingredientsAdditions={ingredientsAdditions}
            appendIngredientsAdditions={appendIngredientsAdditions}
            removeIngredientsAdditions={removeIngredientsAdditions}
            resetEdit={singleIngredientCancelHandler}
          />
        </div>
      </DsmGrid>
      <Spacer height="12px" />
      {isContinuousGrowth &&
        <>
          <DairyV2FeedMothersMilkComponent
            formType={formType}
            fieldItemPrefix={`${fieldItemPrefix}.mothersMilk`}
            currentStageData={dairyV2FeedData}
            isMotherMilkEdit={isMotherMilkEdit}
            setIsMotherMilkEdit={setIsMotherMilkEdit}
            resetEdit={mothersMilkCancelHandler}
          />
          <Spacer />
        </>}
      <p className={classes.headingTitleVariant}>
        {intl.formatMessage({ id: "SUSTELL.STAGE.FEED.FRESH_GRASS" })}
      </p>
      <DairyV2FeedFreshGrassComponent
        formType={formType}
        fieldItemPrefix={fieldItemPrefix}
        currentStageData={dairyV2FeedData}
        isContinuousGrowth={isContinuousGrowth}
        isFreshGrassEdit={isFreshGrassEdit}
        setIsFreshGrassEdit={setIsFreshGrassEdit}
        freshGrass={freshGrass}
        appendFreshGrass={appendFreshGrass}
        removeFreshGrass={removeFreshGrass}
        resetEdit={freshGrassCancelHandler}
        resetFreshGrassAdditionToDefaultValues={resetFreshGrassAdditionToDefaultValues}
      />
      <DsmButtonControlGroup
        cancelHandler={handleDialogCancelClick}
        saveHandler={handleCancel}
        saveLabel={intl.formatMessage({ id: 'GENERAL.CONFIRM' })}
      />
    </DialogContainer>
  )
}

export default DairyV2FeedDialog;
